import type {
  LineServiceMode,
  OrderChannel,
  OrderSource,
  ServiceMode,
} from '../contracts/operating-context.ts';
import type { OrderLineSnapshot } from './product-selection.ts';

export type { OrderChannel, OrderSource, ServiceMode } from '../contracts/operating-context.ts';

export interface CustomerSubmission {
  readonly displayedTotalMinor: number;
  readonly currency: string;
  readonly paymentReference?: string;
}

export interface OrderIntakeIntent {
  readonly intakeId: string;
  readonly businessDate: string;
  readonly source: OrderSource;
  readonly channel: OrderChannel;
  readonly createdAt: string;
  readonly createdBy?: { readonly deviceId?: string; readonly operatorId?: string };
  readonly customerSubmission?: CustomerSubmission;
  readonly serviceMode?: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
  readonly lines: readonly OrderLineSnapshot[];
}

export interface OrderIntakeContext {
  readonly storeReady: boolean;
  readonly reviewRequired: boolean;
}

export interface IntakeReviewEvidence {
  readonly operatorId: string;
  readonly verifiedAt: string;
  readonly authoritativeTotalMinor: number;
  readonly currency: string;
  readonly priceMatched: boolean;
  readonly priceDifferenceMinor: number;
}

export interface OrderIntakeEnvelope {
  readonly status: 'pending_review' | 'accepted';
  readonly reviewStatus: 'pending' | 'confirmed' | 'not_required';
  readonly intakeId: string;
  readonly businessDate: string;
  readonly source: OrderSource;
  readonly channel: OrderChannel;
  readonly createdAt: string;
  readonly createdBy?: { readonly deviceId?: string; readonly operatorId?: string };
  readonly customerSubmission?: CustomerSubmission;
  readonly reviewEvidence?: IntakeReviewEvidence;
  readonly serviceMode?: ServiceMode;
  readonly lineServiceModes?: readonly LineServiceMode[];
  readonly lines: readonly OrderLineSnapshot[];
}

interface StoredIntake {
  readonly signature: string;
  readonly envelope: OrderIntakeEnvelope;
}

const cloneLine = (line: OrderLineSnapshot): OrderLineSnapshot => ({
  ...line,
  selectorKeys: [...line.selectorKeys],
  productSnapshot: {
    ...line.productSnapshot,
    selectedOptions: line.productSnapshot.selectedOptions.map((option) => ({ ...option })),
  },
});

const cloneServiceModes = (value: readonly LineServiceMode[] | undefined): readonly LineServiceMode[] | undefined =>
  value?.map((entry) => ({ ...entry }));

const cloneEnvelope = (envelope: OrderIntakeEnvelope): OrderIntakeEnvelope => ({
  ...envelope,
  createdBy: envelope.createdBy ? { ...envelope.createdBy } : undefined,
  customerSubmission: envelope.customerSubmission ? { ...envelope.customerSubmission } : undefined,
  reviewEvidence: envelope.reviewEvidence ? { ...envelope.reviewEvidence } : undefined,
  lineServiceModes: cloneServiceModes(envelope.lineServiceModes),
  lines: envelope.lines.map(cloneLine),
});

const stableValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stableValue);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, entry]) => [key, stableValue(entry)]),
    );
  }
  return value;
};

const signatureOf = (intent: OrderIntakeIntent, context: OrderIntakeContext): string =>
  JSON.stringify(stableValue({ intent, reviewRequired: context.reviewRequired }));

const isServiceMode = (value: unknown): value is ServiceMode => value === 'dine-in' || value === 'takeaway';

const assertIntent = (intent: OrderIntakeIntent): void => {
  if (!intent.intakeId || !intent.businessDate || !intent.source || !intent.channel) throw new Error('ORDER_INTAKE_IDENTITY_REQUIRED');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(intent.businessDate)) throw new Error('ORDER_INTAKE_BUSINESS_DATE_INVALID');
  if (!Number.isFinite(Date.parse(intent.createdAt))) throw new Error('ORDER_INTAKE_CREATED_AT_INVALID');
  if (!Array.isArray(intent.lines) || intent.lines.length === 0) throw new Error('INVALID_ORDER_INTAKE_LINES');
  if (intent.serviceMode !== undefined && !isServiceMode(intent.serviceMode)) throw new Error('ORDER_INTAKE_SERVICE_MODE_INVALID');

  const lineIds = new Set<string>();
  for (const line of intent.lines) {
    if (!line.lineId?.trim() || line.lineId !== line.lineId.trim()) throw new Error('ORDER_INTAKE_LINE_ID_INVALID');
    if (lineIds.has(line.lineId)) throw new Error('ORDER_INTAKE_LINE_ID_DUPLICATE');
    lineIds.add(line.lineId);
    if (!Number.isSafeInteger(line.quantity) || line.quantity <= 0) throw new Error('INVALID_ORDER_INTAKE_QUANTITY');
  }

  const serviceLineIds = new Set<string>();
  for (const entry of intent.lineServiceModes ?? []) {
    if (!entry.lineId?.trim() || entry.lineId !== entry.lineId.trim()) throw new Error('ORDER_INTAKE_LINE_SERVICE_ID_INVALID');
    if (serviceLineIds.has(entry.lineId)) throw new Error('ORDER_INTAKE_LINE_SERVICE_DUPLICATE');
    if (!lineIds.has(entry.lineId)) throw new Error('ORDER_INTAKE_LINE_SERVICE_UNKNOWN');
    if (!isServiceMode(entry.serviceMode)) throw new Error('ORDER_INTAKE_LINE_SERVICE_MODE_INVALID');
    serviceLineIds.add(entry.lineId);
  }

  const submission = intent.customerSubmission;
  if (submission) {
    if (!Number.isSafeInteger(submission.displayedTotalMinor) || submission.displayedTotalMinor < 0 || !submission.currency) {
      throw new Error('CUSTOMER_SUBMISSION_INVALID');
    }
  }
};

export function createOrderIntake() {
  const stored = new Map<string, StoredIntake>();

  return {
    accept(intent: OrderIntakeIntent, context: OrderIntakeContext): OrderIntakeEnvelope {
      if (!context.storeReady) throw new Error('STORE_NOT_READY_FOR_ORDER_INTAKE');
      assertIntent(intent);

      const signature = signatureOf(intent, context);
      const existing = stored.get(intent.intakeId);
      if (existing) {
        if (existing.signature !== signature) throw new Error('ORDER_INTAKE_ID_CONFLICT');
        return cloneEnvelope(existing.envelope);
      }

      const envelope: OrderIntakeEnvelope = {
        status: context.reviewRequired ? 'pending_review' : 'accepted',
        reviewStatus: context.reviewRequired ? 'pending' : 'not_required',
        intakeId: intent.intakeId,
        businessDate: intent.businessDate,
        source: intent.source,
        channel: intent.channel,
        createdAt: intent.createdAt,
        createdBy: intent.createdBy ? { ...intent.createdBy } : undefined,
        customerSubmission: intent.customerSubmission ? { ...intent.customerSubmission } : undefined,
        serviceMode: intent.serviceMode,
        lineServiceModes: cloneServiceModes(intent.lineServiceModes),
        lines: intent.lines.map(cloneLine),
      };

      stored.set(intent.intakeId, { signature, envelope });
      return cloneEnvelope(envelope);
    },

    confirm(
      intakeId: string,
      evidence: Omit<IntakeReviewEvidence, 'priceMatched' | 'priceDifferenceMinor'>,
    ): OrderIntakeEnvelope {
      const current = stored.get(intakeId);
      if (!current) throw new Error('ORDER_INTAKE_NOT_FOUND');
      if (current.envelope.reviewStatus === 'not_required') return cloneEnvelope(current.envelope);
      if (current.envelope.reviewStatus === 'confirmed') return cloneEnvelope(current.envelope);
      const submission = current.envelope.customerSubmission;
      if (!submission) throw new Error('CUSTOMER_ORDER_SUBMISSION_MISSING');
      if (submission.currency !== evidence.currency) throw new Error('CUSTOMER_ORDER_CURRENCY_MISMATCH');
      if (!Number.isSafeInteger(evidence.authoritativeTotalMinor) || evidence.authoritativeTotalMinor < 0) {
        throw new Error('ORDER_REVIEW_TOTAL_INVALID');
      }
      if (!Number.isFinite(Date.parse(evidence.verifiedAt))) throw new Error('ORDER_REVIEW_TIME_INVALID');

      const difference = evidence.authoritativeTotalMinor - submission.displayedTotalMinor;
      const accepted: OrderIntakeEnvelope = {
        ...current.envelope,
        status: 'accepted',
        reviewStatus: 'confirmed',
        reviewEvidence: {
          ...evidence,
          priceMatched: difference === 0,
          priceDifferenceMinor: difference,
        },
      };
      stored.set(intakeId, { signature: current.signature, envelope: accepted });
      return cloneEnvelope(accepted);
    },
  };
}
